



































































































import { User } from '@/types/GlobalTypes';
import { get, isEmpty } from 'lodash';
import CoText from '@/components/Atoms/co-text/CoText.vue';
import CoSkeleton from '@/components/Atoms/co-skeleton/CoSkeleton.vue';
import EventBus from '../../../eventBus';
import { isUserOnline } from '../../../utils/realtimePresence';
import CoThumbnail from '../co-thumbnail/CoThumbnail.vue';
import CoDate from '../co-date/CoDate.vue';

export default {
    name: 'CoUser',
    components: {
        CoThumbnail,
        CoText,
        CoDate,
        CoSkeleton,
    },
    props: {
        User: {
            type: Object as () => User,
            default: () => ({}), // Default to an empty object to avoid undefined errors
        },
        Loading: {
            type: Boolean,
            default: false,
        },
        showLink: {
            type: Boolean,
            default: true,
        },
        linkType: {
            type: String,
            default: 'auto',
            validator: (value) => ['auto', 'admin', 'user'].includes(value),
        },
        showHeadline: {
            type: Boolean,
            default: false,
        },
        showThumbnail: {
            type: Boolean,
            default: true,
        },
        showName: {
            type: Boolean,
            default: true,
        },
        Timestamp: {
            type: [Number, String, Date],
            default: null,
        },
        Size: {
            type: String,
            default: 'md', // Default size can be 'sm', 'ms', 'md'
            validator: (value) => ['sm', 'ms', 'md'].includes(value),
        },
        lightbox: {
            type: Boolean,
            default: false,
        },
        linkColorClass: {
            type: String,
            default: '',
        },
        linkTextSize: {
            type: String,
            default: null,
            validator: (value) => ['p1', 'p2', 'p3'].includes(value),
        },
    },
    data() {
        return {
            // Presence state is updated by user-targeted event bus events.
            isOnline: null,
            presenceEventName: null,
        };
    },
    computed: {
        Name() {
            let name = this.User?.Name;
            if (!name) {
                const firstname = this.User?.FirstName || this.User?.Firstname || '';
                const lastname = this.User?.LastName || this.User?.Lastname || '';
                name = `${firstname} ${lastname}`.trim();
            }
            if (!name || this.isDeleted) {
                name = this.$t('labels.usernotavailable');
            }
            return name;
        },
        nameVariant() {
            if (this.userStatus === 'guest' && this.linkType !== 'admin') {
                return 'mute';
            }

            if (this.isDeleted) {
                return 'mute';
            }

            if (!this.showLink) {
                return 'normal';
            }
            if (this.linkColorClass?.length > 0) {
                return this.linkColorClass;
            }
            return 'primary';
        },
        nameSize() {
            if (this.linkTextSize) {
                return this.linkTextSize;
            }
            if (this.Size === 'sm') {
                return 'p3';
            }
            if (this.Size === 'ms') {
                return 'p2';
            }
            return 'p1';
        },
        Slug() {
            return this.User?.Slug || '';
        },
        ImageURL() {
            return this.User?.ImageURL || null;
        },
        Headline() {
            return this.User?.Bio || null;
        },
        userStatus() {
            return this.User?.Status === 7 ? 'guest' : null; // Assuming 7 is the status for guest users
        },
        asAdmin() {
            return this.$store?.state?.me?.Permissions?.includes('space_admin'); // viewing user is an admin
        },
        isDeleted() {
            const DELETED_USER_STATUS_CODES = [2, 6, 8]; // 2: deleted, 6: rejected, 8: banned

            if (DELETED_USER_STATUS_CODES.includes(this.User?.Status)) {
                return true;
            }

            // Check if the user is deleted (no user data available)
            if (!this.User || isEmpty(this.User)) {
                return true;
            }

            if (
                !this.User?.Name &&
                !this.User?.FirstName &&
                !this.User?.Firstname &&
                !this.User?.LastName &&
                !this.User?.Lastname
            ) {
                return true;
            }

            return false;
        },
        viewingUserSpaceID() {
            // Get the space ID of the viewing user
            return this.$store?.state?.space?.ID || null;
        },
        Icon() {
            if (this.isDeleted) return { icon: 'eye-slash' }; // Icon-label for deleted users
            if (this.userStatus === 'guest') return { icon: 'lock', color: 'var(--c-lemon)' }; // Icon-label for guest users
            if (
                (this.User?.Space?.FaviconURL || this.User?.Space?.PrimaryColor) &&
                this.viewingUserSpaceID !== this.User?.Space?.ID
            ) {
                // If the user has a space, use its favicon or primary color
                return {
                    color: this.User?.Space?.PrimaryColor || null,
                    imageURL: this.User?.Space?.FaviconURL || null,
                };
            }
            return null;
        },
    },
    mounted() {
        this.subscribeToPresence();
    },
    beforeDestroy() {
        this.unsubscribeFromPresence();
    },
    watch: {
        'User.ID': 'handleUserIdChange',
    },
    methods: {
        // Define methods to handle user interactions
        get,
        presenceEventNameForUser(userId) {
            if (userId === null || userId === undefined || userId === '') return null;

            return `USER_PRESENCE_${userId}`;
        },
        subscribeToPresence() {
            const eventName = this.presenceEventNameForUser(this.User?.ID);

            if (!eventName || eventName === this.presenceEventName) return;

            this.isOnline = isUserOnline(this.User?.ID);
            this.presenceEventName = eventName;
            EventBus.$on(eventName, this.handlePresenceUpdate);
        },
        unsubscribeFromPresence() {
            if (!this.presenceEventName) return;

            EventBus.$off(this.presenceEventName, this.handlePresenceUpdate);
            this.presenceEventName = null;
        },
        handlePresenceUpdate(payload) {
            const currentUserId = this.User?.ID;
            const payloadUserId = payload?.userId;

            if (currentUserId === null || currentUserId === undefined) return;
            if (payloadUserId === null || payloadUserId === undefined) return;
            if (String(payloadUserId) !== String(currentUserId)) return;

            this.isOnline = payload.online === true;
        },
        handleUserIdChange(newUserId, oldUserId) {
            if (newUserId === oldUserId) return;

            this.unsubscribeFromPresence();
            this.isOnline = null;
            this.subscribeToPresence();
        },
        clickHandler() {
            if (!get(this.User, 'ID', null)) return; // Ensure User ID exists before proceeding
            // Handle click events based on the Link prop and the status of the viewing user
            if (!this.showLink || this.isDeleted || this.Loading) return; // Prevent interaction if user is deleted or loading

            if (this.linkType !== 'admin' && this.userStatus === 'guest') {
                return;
            }

            // navigate to internal links
            if (this.linkType !== 'admin' || this.$store?.state?.isInMobile || !this.asAdmin) {
                // as a user or in mobile app
                this.$router.push({ path: `/profile/${get(this.User, 'Slug')}` });
            } else {
                // as an admin
                this.$router.push({ path: `/admin/community/member/view/${get(this.User, 'ID')}` });
            }
        },
    },
};
